/**
 * Таблица экрана «Входящие» (ТЗ §7 и §8.6): статус, источник, дата поступления,
 * заявитель и ссылки на заведённые дела.
 *
 * Компонент только рисует строки — загрузку, повтор и разбор ведёт InboxPage.
 */

import type { MouseEvent } from 'react';
import type { InboxItemDto, InboxStatus } from '@/api/types';
import { fileSize, fmtDateTime } from '@/lib/format';
import { INBOX_STATUS_LABELS, sourceLabel } from '@/lib/stages';

/**
 * Чип статуса. Красного варианта у `.stage-chip` нет, поэтому ошибка показана
 * чипом `.chip.bad` — тот же радиус и размер, но красная палитра.
 */
const STATUS_CHIP: Readonly<Record<InboxStatus, string>> = {
    new: 'stage-chip s-registered',
    processing: 'stage-chip s-qualified',
    needs_confirm: 'stage-chip s-approval',
    done: 'stage-chip s-sent',
    failed: 'chip bad',
};

export function inboxStatusChipClass(status: InboxStatus): string {
    return STATUS_CHIP[status] ?? 'stage-chip';
}

/** Вторая строка в названии: заявитель, контакт, вложения, кто внёс. */
function metaLine(item: InboxItemDto): string {
    const parts: string[] = [];
    if (item.applicant_name) parts.push(item.applicant_name);
    if (item.applicant_contact) parts.push(item.applicant_contact);
    const files = item.attachments ?? [];
    for (const file of files) parts.push(`${file.name} · ${fileSize(file.size)}`);
    if (!item.has_body && files.length === 0) parts.push('текст обращения не приложен');
    if (item.created_by_name) parts.push(`внёс ${item.created_by_name}`);
    return parts.join(' · ');
}

function stop(fn: () => void) {
    return (event: MouseEvent) => {
        event.stopPropagation();
        fn();
    };
}

export interface InboxTableProps {
    items: InboxItemDto[];
    /** Открыть заведённое дело. */
    onOpenCase: (caseId: string) => void;
    /** Экран «В обращении найдено N самостоятельных обращений». */
    onResolve: (item: InboxItemDto) => void;
    /** Показать ход разбора для незавершённых входящих. */
    onWatch: (item: InboxItemDto) => void;
    onRetry: (item: InboxItemDto) => void;
    retryingId?: string | null;
}

export default function InboxTable({
    items,
    onOpenCase,
    onResolve,
    onWatch,
    onRetry,
    retryingId,
}: InboxTableProps) {
    if (items.length === 0) {
        return <div className="list-empty">Во входящих ничего не найдено. Измените поиск или фильтр либо добавьте обращение кнопкой «Добавить обращение».</div>;
    }

    return (
        <>
            {items.map((item) => {
                // Именно const: TypeScript сохраняет сужение типа внутри обработчиков только для него.
                const activate: (() => void) | null =
                    item.status === 'needs_confirm'
                        ? () => onResolve(item)
                        : item.status === 'new' || item.status === 'processing'
                          ? () => onWatch(item)
                          : item.cases.length > 0
                            ? () => onOpenCase(item.cases[0].case_id)
                            : null;

                const meta = metaLine(item);

                return (
                    <div
                        key={item.id}
                        className="list-row"
                        role={activate ? 'button' : undefined}
                        tabIndex={activate ? 0 : undefined}
                        onClick={activate ?? undefined}
                        onKeyDown={
                            activate
                                ? (event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                          event.preventDefault();
                                          activate();
                                      }
                                  }
                                : undefined
                        }
                    >
                        <span className="lr-id">{fmtDateTime(item.received_at)}</span>
                        <span className={inboxStatusChipClass(item.status)}>
                            {INBOX_STATUS_LABELS[item.status]}
                        </span>
                        <span className="lr-title">
                            {item.subject?.trim() || 'Без темы'}
                            {meta ? (
                                <>
                                    <br />
                                    <span className="my-appl">{meta}</span>
                                </>
                            ) : null}
                            {item.status === 'failed' && item.error ? (
                                <span className="period-error show">{item.error}</span>
                            ) : null}
                        </span>
                        <span className="lr-exec">{sourceLabel(item.source)}</span>

                        {item.cases.map((ref) => (
                            <button
                                key={ref.case_id}
                                type="button"
                                className="link-btn"
                                title={ref.title}
                                onClick={stop(() => onOpenCase(ref.case_id))}
                            >
                                {ref.number}
                            </button>
                        ))}

                        <span className="review-wrap">
                            {item.status === 'failed' ? (
                                <button
                                    type="button"
                                    className="act-btn"
                                    disabled={retryingId === item.id}
                                    onClick={stop(() => onRetry(item))}
                                >
                                    {retryingId === item.id ? 'Отправляем…' : 'Повторить'}
                                </button>
                            ) : null}
                            {item.status === 'needs_confirm' ? (
                                <button
                                    type="button"
                                    className="act-btn primary"
                                    onClick={stop(() => onResolve(item))}
                                >
                                    Разобрать
                                </button>
                            ) : null}
                            {item.status === 'new' || item.status === 'processing' ? (
                                <button type="button" className="act-btn" onClick={stop(() => onWatch(item))}>
                                    Ход разбора
                                </button>
                            ) : null}
                        </span>
                    </div>
                );
            })}
        </>
    );
}
